import styled, { useTheme } from 'foundations-theming/utils';
import { useIconSize } from 'foundations-assets/icons/useIconSize';
import { IconCircle } from '../IconCircle';
import { toPx } from '../../../utils/toPx';
import { Clickable } from '../../components/Clickable/Clickable';
import { getKeyFromStep } from './StepIndicator.utils';
import { TEXT_VISUALLY_HIDDEN_STYLES } from '../../../components/Text/Text.styles';
import { useWizardHeaderContext } from '../Wizard/WizardHeaderProvider';
import { deepMerge } from '../../utils/deepMerge';
import { BlankIcon } from 'foundations-assets/icons/components/BlankIcon';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledStepItem = styled.div.withConfig({
  displayName: "StepIndicatorSteps__StyledStepItem",
  componentId: "sc-1xml9oz-0"
})(({
  $stepStatus,
  $isCustomized,
  $marginLeft,
  $marginTop,
  $direction,
  $use,
  $styleOverrides,
  theme
}) => {
  return deepMerge(Object.assign({}, theme.typography['heading-25'], {
    color: theme.color['text-core-default'],
    textAlign: 'center',
    wordWrap: 'break-word',
    marginLeft: $marginLeft,
    marginTop: $marginTop
  }, ($use === 'compact' || $use === 'flush') && TEXT_VISUALLY_HIDDEN_STYLES, $stepStatus === 'active' && $isCustomized && Object.assign({}, theme.typography['heading-25']), $direction === 'vertical' && {
    flexGrow: 1,
    paddingBottom: theme.space['700'],
    textAlign: 'left'
  }, {
    '& small': {
      textTransform: 'none'
    }
  }), $styleOverrides);
});
const StyledLine = styled.div.withConfig({
  displayName: "StepIndicatorSteps__StyledLine",
  componentId: "sc-1xml9oz-1"
})(({
  $isFirst,
  $isLast,
  $direction,
  $stepStatus,
  $use,
  theme
}) => {
  return Object.assign({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    width: '100%'
  }, $direction === 'vertical' && {
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    width: 'auto'
  }, {
    '&::before': Object.assign({
      borderTop: `${theme.borderWidth['200']} solid`,
      content: `" "`,
      position: 'relative',
      width: '100%'
    }, $direction === 'horizontal' && {
      flex: '1 1 100%',
      display: 'inline-block'
    }, $direction === 'vertical' && {
      display: 'none'
    }, $isFirst && {
      visibility: 'hidden'
    }, $use === 'flush' && {
      display: 'none'
    }),
    '&::after': Object.assign({
      borderTop: `${theme.borderWidth['200']} solid`,
      content: `" "`,
      position: 'relative',
      width: '100%'
    }, $direction === 'horizontal' && {
      flex: '1 1 100%',
      display: 'inline-block'
    }, $direction === 'vertical' && {
      flex: '1 0 auto',
      borderLeft: `${theme.borderWidth['200']} solid`,
      borderTop: 0,
      content: `" "`,
      display: 'inline-block',
      position: 'relative',
      width: 'auto'
    }, $isLast && {
      visibility: 'hidden'
    }, $use === 'flush' && $isLast && {
      display: 'none'
    }, $stepStatus === 'active' && {
      color: theme.color['border-core-default']
    })
  });
});
const StyledCircle = styled.span.withConfig({
  displayName: "StepIndicatorSteps__StyledCircle",
  componentId: "sc-1xml9oz-2"
})(({
  $direction,
  $isClickable,
  theme
}) => {
  return Object.assign({
    flex: '0 0 auto',
    paddingTop: theme.space['100'],
    paddingBottom: theme.space['100'],
    paddingLeft: theme.space['0'],
    paddingRight: theme.space['0'],
    transition: 'opacity 150ms ease-in-out',
    lineHeight: 0,
    '> *': {
      verticalAlign: 'top'
    }
  }, $isClickable && {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.65
    }
  }, $direction === 'vertical' && {
    paddingTop: theme.space['0'],
    paddingBottom: theme.space['0']
  });
});
const StyledStepIndicatorWrapper = styled(props => {
  const Component = props.$element;

  // We have to manually filter out transient props because Styled Components isn't filtering them out automatically and `shouldForwardProp` isn't working
  const filteredProps = Object.entries(props).reduce((acc, [key, value]) => {
    if (!key.startsWith('$')) {
      acc[key] = value;
    }
    return acc;
  }, {});
  return /*#__PURE__*/_jsx(Component, Object.assign({}, filteredProps));
})(({
  $direction,
  $isLast,
  $stepStatus,
  $use,
  theme
}) => {
  return Object.assign({
    display: 'flex',
    flex: '1 1 0%',
    justifyContent: 'center',
    color: $stepStatus === 'active' || $stepStatus === 'complete' ? theme.color['fill-brand-default'] : theme.color['border-core-default']
  }, $direction === 'horizontal' && Object.assign({
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center'
  }, $isLast && $use === 'flush' && {
    flex: '0 0 auto'
  }), $direction === 'vertical' && {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexBasis: 'auto',
    position: 'relative',
    '@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none)': {
      borderTopWidth: theme.borderWidth['100'],
      borderTopStyle: 'solid',
      borderTopColor: theme.color['fill-transparent-default']
    },
    // resets ^ for Edge
    '@supports (-ms-ime-align: auto)': {
      borderTop: theme.borderWidth['0']
    }
  }, {
    '&:focus': {
      overflow: 'hidden'
    }
  });
});
const DefaultStepIconComponent = ({
  defaultIcon
}) => defaultIcon;
export default function StepIndicatorSteps({
  circleSize,
  direction,
  onStepClick,
  stepIndex,
  stepNames,
  StepIconComponent = DefaultStepIconComponent,
  isCustomized,
  use
}) {
  const theme = useTheme();
  let stepStatus = 'incomplete';
  const circleSizeValue = useIconSize(circleSize);
  const wizardHeaderContext = useWizardHeaderContext();
  return stepNames.map((stepName, index) => {
    var _wizardHeaderContext$;
    if (index < stepIndex && stepIndex > 0) {
      stepStatus = 'complete';
    } else if (index === stepIndex) {
      stepStatus = 'active';
    } else if (stepStatus === 'active') {
      // If stepStatus is "active", then all subsequent steps are incomplete
      stepStatus = 'incomplete';
    }
    const stepIcon = /*#__PURE__*/_jsx(IconCircle, {
      legacy: true,
      color: stepStatus === 'complete' ? 'icon-core-on-fill-default' : 'icon-secondary-default',
      backgroundColor: stepStatus === 'complete' ? 'fill-brand-default' : 'fill-field-default-alt',
      borderColor: stepStatus === 'incomplete' ? 'border-core-default' : 'border-brand-default',
      borderWidth: "200",
      icon: stepStatus === 'complete' ? SuccessIcon : BlankIcon,
      size: circleSize
    });
    const getStepAriaLabel = getKeyFromStep(stepName);
    const role = onStepClick && index !== stepIndex ? 'button' : 'listitem';
    const ariaLabel = stepStatus === 'complete' ? `${getStepAriaLabel} completed` : getStepAriaLabel;
    const lineHeightOffset = parseInt(theme.typography['heading-25'].lineHeight, 10) / 2;
    const labelSeparationX = 10;
    const pixelSize = circleSize && circleSize !== 'inherit' ? parseInt(circleSizeValue, 10) : 14;
    const itemOffsetX = direction === 'vertical' ? toPx(pixelSize * 2 + labelSeparationX) : undefined; // undefined instead of null due to this error: Type 'null' is not assignable to type 'MarginTopProperty<string | number> | undefined'
    const itemOffsetY = direction === 'vertical' ? toPx(pixelSize - lineHeightOffset) : undefined;
    return /*#__PURE__*/_jsxs(StyledStepIndicatorWrapper, {
      $element: onStepClick && index !== stepIndex ? Clickable : 'div',
      role: role,
      "aria-current": stepStatus === 'active' ? 'step' : false,
      "aria-label": ariaLabel,
      $direction: direction,
      $stepStatus: stepStatus,
      $isLast: index === stepNames.length - 1,
      $use: use,
      onClick: onStepClick && index !== stepIndex ? () => onStepClick(index) : undefined,
      children: [/*#__PURE__*/_jsx(StyledLine, {
        $direction: direction,
        $use: use,
        $stepStatus: stepStatus,
        $isFirst: index === 0,
        $isLast: index === stepNames.length - 1,
        children: /*#__PURE__*/_jsx(StyledCircle, {
          $direction: direction,
          $isClickable: !!onStepClick && index !== stepIndex,
          children: /*#__PURE__*/_jsx(StepIconComponent, {
            stepIndex: stepIndex,
            stepStatus: stepStatus,
            index: index,
            circleSize: circleSize,
            defaultIcon: stepIcon
          })
        })
      }), /*#__PURE__*/_jsx(StyledStepItem, {
        $marginLeft: itemOffsetX,
        $marginTop: itemOffsetY,
        $direction: direction,
        $use: use,
        $stepStatus: stepStatus,
        $isCustomized: isCustomized,
        $styleOverrides: (_wizardHeaderContext$ = wizardHeaderContext === null || wizardHeaderContext === void 0 ? void 0 : wizardHeaderContext.stepIndicatorStyleOverrides) !== null && _wizardHeaderContext$ !== void 0 ? _wizardHeaderContext$ : {},
        children: /*#__PURE__*/_jsx("span", {
          children: stepName
        })
      })]
    }, getKeyFromStep(stepName));
  });
}